// React
import { ReactElement } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Context
import { useDashboard } from '@utmb/contexts/DashboardContext';
// Interfaces
import { DashboardMemberWorldSerieResultView } from '@utmb/types/index';

// Design System
import { Font } from '@utmb/design-system/text/font';
// Types
import { RunnerCardsProps } from '@utmb/types/runner-cards';
// Styles
import styles from './achievementList.module.scss';
import { MyTable } from '@utmb/design-system/my-table/MyTable';
import { MyTableCell } from '@utmb/design-system/my-table/MyTableCell';
import { MyTableHeader } from '@utmb/design-system/my-table/MyTableHeader';
import { MyTableRow } from '@utmb/design-system/my-table/MyTableRow';
import { pIdToCloudinary } from '@utmb/utils/cloudinary';
import { CloudinaryTransformEnum } from '@utmb/types/cloudinary-transform.enum';
import { Image } from '@utmb/design-system/image/Image';
import { Flag } from '@utmb/design-system/flag/Flag';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { formatUnit } from '@utmb/utils/unit';
import { VALID_INDEX_THRESHOLD } from '@utmb/utils/utmb-index.constants';
import { usePreference } from '@utmb/contexts/PreferenceContext';
import { GenderEnum } from '@utmb/types/enum/GenderEnum';
import { ContentCardReward } from '../my-utmb/content-card/ContentCardReward';
import UserLoyaltyHistoryView from '@utmb/types/view/UserLoyaltyHistoryView';

const AchievementsList = (props: RunnerCardsProps & { fullResults?: DashboardMemberWorldSerieResultView[] }): ReactElement | null => {
    const { limit, fullResults } = props;

    const { dashboardMember, isAttached } = useDashboard();
    const { t } = useTranslation('myutmb');

    const {
        preferences: { preferredUnit },
    } = usePreference();

    if (!dashboardMember) {
        return null;
    }

    if ((!fullResults && !dashboardMember.worldSeriesResults.length) || !isAttached) {
        return (
            <div className={styles.achievement_list}>
                <Font.OxaniumRegular mobile="14" desktop="16" className={styles.stats_item_label}>
                    {t('myutmb:myUtmb.achievement.noResult')}
                </Font.OxaniumRegular>
            </div>
        );
    }
    let results = fullResults ?? dashboardMember.worldSeriesResults;
    if (limit) results = results.slice(0, limit);

    const getLoyaltyPointsForRace = (item: DashboardMemberWorldSerieResultView): UserLoyaltyHistoryView | undefined => {
        return dashboardMember.loyalty?.history.find((target) => target.raceYearId === item.raceYearId);
    };

    return (
        // <ul className={styles.achievement_list}>
        //     {results.map((item: DashboardMemberWorldSerieResultView, index) => (
        //         <li className={styles.achievement_item} key={index}>
        //             <AchievementCard {...item} utmbIndexOnly={!item.logo} />
        //         </li>
        //     ))}
        // </ul>
        <>
            <MyTable className={styles.table}>
                <MyTableHeader className={styles.table_header}>
                    <MyTableCell hiddenOnMobile column={4}>
                        {t('myutmb:myUtmb.achievement.raceLabel')}
                    </MyTableCell>
                    <MyTableCell hiddenOnMobile column={2}>
                        {t('myutmb:myUtmb.achievement.distanceLabel')}
                    </MyTableCell>
                    <MyTableCell hiddenOnMobile column={1}>
                        {t('myutmb:myUtmb.achievement.timeLabel')}
                    </MyTableCell>
                    <MyTableCell hiddenOnMobile column={2}>
                        {t('myutmb:myUtmb.achievement.rankLabel')}
                    </MyTableCell>
                    <MyTableCell hiddenOnMobile column={1}>
                        <Image
                            objectFit="contain"
                            layout="fixed"
                            width={75}
                            height={15}
                            maxWidth={300}
                            image={pIdToCloudinary('/Common/categories/index_bg', 'UTMB Index Race')}
                            addOptions={[CloudinaryTransformEnum.TRIM]}
                        />
                    </MyTableCell>
                    <MyTableCell hiddenOnMobile column={2}>
                        {t('myutmb:myUtmb.achievement.rewardsLabel')}
                    </MyTableCell>
                </MyTableHeader>

                {results.map((item: DashboardMemberWorldSerieResultView) => {
                    const loyaltyHistoRace = getLoyaltyPointsForRace(item);
                    return (
                        <MyTableRow
                            key={item.raceName + '-' + item.year}
                            className={styles.customTableRow}
                            height={50}
                            showDetailsClassName={styles.customShowDetailTableRow}
                        >
                            <MyTableCell column={4}>
                                <div className={styles.mainFlexRowCellContent}>
                                    <div className={styles.wrapLogo}>
                                        <Image
                                            objectFit="contain"
                                            layout="fixed"
                                            width={50}
                                            height={50}
                                            maxWidth={300}
                                            image={
                                                item.utmbEventStatus
                                                    ? pIdToCloudinary(item.logoColor, 'UTMB World Series')
                                                    : pIdToCloudinary('/Common/categories/index_bg', 'UTMB Index Race')
                                            }
                                            addOptions={[CloudinaryTransformEnum.TRIM]}
                                        />
                                    </div>
                                    <div className={styles.wrapRaceTitle}>
                                        <Font.OxaniumRegular className={styles.raceTitle} as="h3">
                                            {item.eventName ?? ''} {item.year ? item.year : ''}
                                            {item.year || item.eventName ? ' - ' : ''}
                                            {item.raceName ?? ''}
                                        </Font.OxaniumRegular>
                                        <div className={styles.wrapDateFlag}>
                                            <Font.OxaniumRegular mobile="13" desktop="15">
                                                {item.date}
                                            </Font.OxaniumRegular>
                                            {item.countryCode ? (
                                                <div title={item.countryName}>
                                                    <Flag className={styles.flag} isoCode={item.countryCode} hideShadow />
                                                </div>
                                            ) : (
                                                <div />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </MyTableCell>
                            <MyTableCell label={t('myutmb:myUtmb.achievement.distanceLabel')} hiddenOnMobile column={2}>
                                <div className={styles.flexRowCellContent}>
                                    <div className={styles.category_logo}>
                                        {item.category ? <PiCategoryLogo piCategory={item.category} isDark={false} /> : null}
                                    </div>

                                    <div>
                                        <Font.OxaniumRegular mobile="13" desktop="15">
                                            {formatUnit(preferredUnit, 'distance', item.distance)}
                                        </Font.OxaniumRegular>{' '}
                                        <Font.OxaniumRegular mobile="13" desktop="15">
                                            {formatUnit(preferredUnit, 'elevationGain', item.elevationGain)}
                                        </Font.OxaniumRegular>
                                    </div>
                                </div>
                            </MyTableCell>
                            <MyTableCell label={t('myutmb:myUtmb.achievement.timeLabel')} hiddenOnMobile column={1}>
                                <Font.OxaniumRegular mobile="13" desktop="15">
                                    {item.time ? <>{item.time}</> : 'DNF'}
                                </Font.OxaniumRegular>
                            </MyTableCell>
                            <MyTableCell label={t('myutmb:myUtmb.achievement.rankLabel')} hiddenOnMobile column={2}>
                                <div className={styles.flexColumnCellContent}>
                                    <Font.OxaniumRegular mobile="13" desktop="15">
                                        {item.rank ? `${item.rank} / ${item.totalRankedRunners}` : '-'}
                                    </Font.OxaniumRegular>
                                    <Font.OxaniumRegular mobile="13" desktop="15">
                                        {item.rank && item.rankGender
                                            ? `${item.rankGender} / ${item.totalRankedGender} ${
                                                  dashboardMember?.information?.gender === GenderEnum.FEMALE ? '♀' : '♂'
                                              }`
                                            : '-'}
                                    </Font.OxaniumRegular>
                                </div>
                            </MyTableCell>

                            <MyTableCell label={t('myutmb:myUtmb.achievement.indexLabel')} hiddenOnMobile column={1}>
                                <Font.OxaniumBold mobile="16" desktop="18">
                                    {!item.index ? '-' : item.index > VALID_INDEX_THRESHOLD ? item.index : t('myutmb:myUtmb.myStones.reason.race')}
                                </Font.OxaniumBold>
                            </MyTableCell>

                            <MyTableCell hiddenOnMobile column={2} className={styles.rewardsCell}>
                                <div className={styles.flexRowCellContent}>
                                    <div className={styles.flexItem}>
                                        <>{item.stonesQuantity && item.rank ? <ContentCardReward type={'STONES'} quantity={item.stonesQuantity} /> : '-'}</>
                                    </div>
                                    {loyaltyHistoRace && loyaltyHistoRace.active ? (
                                        <div className={styles.flexItem}>
                                            <ContentCardReward
                                                type={'POINTS'}
                                                quantity={loyaltyHistoRace.points}
                                                loyaltyLevel={dashboardMember.loyalty?.currentLevel}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </MyTableCell>
                        </MyTableRow>
                    );
                })}
            </MyTable>
        </>
    );
};

export default AchievementsList;
